import { Dispatch, ReactNode, SetStateAction, useMemo } from 'react';
import { css } from '@emotion/react';
import type { ScheduleActivityItem } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_activity.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { TableColumnConfig, Table, TableActionsProps, ManualFilters } from '@frontend/design-system';
import { formatDateByRecency } from '../../utils/date-helpers';
import { TableHeaderWithTimezoneInfo } from '../v3/table-header-with-timezone-info';
import { AppointmentTimeCellRenderer } from './appointment-time-cell-renderer';
import { ACTIVITY_ROW_CLICK_ALLOWED_STATUSES, activityTypeLabels } from './constants';
import { FiltersDrawer } from './filters/filters-drawer';
import { hasActiveFilters } from './filters/helpers';
import type { FilterOptions, FilterState } from './filters/types';
import { PatientCellRenderer } from './patient-cell-renderer';
import { ServiceAndAppointmentTypeCellRenderer } from './service-and-appointment-type-cell-renderer';
import { SourceCellRenderer } from './source-cell-renderer';
import { StatusCellRenderer } from './status-cell-renderer';
import type { ActivityType } from './types';
import type { PageConfig } from './index';

type Props = {
  data: ScheduleActivityItem[];
  totalItems: number;
  pageConfig: PageConfig;
  setPageConfig: Dispatch<SetStateAction<PageConfig>>;
  filters: FilterState;
  filterOptions: FilterOptions;
  setFilters: Dispatch<SetStateAction<FilterState>>;
  isLoading?: boolean;
  locationId: string;
  showServiceColumn?: boolean;
  locationDropdown?: ReactNode;
  onRefetch: () => void;
  trackFiltersApplied: (values: FilterState) => void;
  onRowClick: (row: ScheduleActivityItem) => void;
};

export const ScheduleActivityTable = ({
  data,
  totalItems,
  pageConfig,
  setPageConfig,
  filters,
  filterOptions,
  setFilters,
  locationId,
  isLoading = false,
  showServiceColumn = false,
  locationDropdown,
  onRefetch,
  trackFiltersApplied,
  onRowClick,
}: Props) => {
  const { t } = useTranslation('schedule-activity');
  const { locationTimezone } = useLocationTimezone(locationId);

  const columnConfig: TableColumnConfig<ScheduleActivityItem>[] = useMemo(
    () => [
      {
        Header: t('Time'),
        id: 'occurredAt',
        minWidth: 220,
        accessor: (row) => row.occurredAt,
        cellRenderer: (row) => formatDateByRecency(row) || '-',
      },
      {
        Header: t('Event'),
        id: 'activityType',
        disableSortBy: true,
        minWidth: 100,
        maxWidth: 140,
        accessor: (row) => row.activityType,
        cellRenderer: (row) => activityTypeLabels[row as ActivityType] || '-',
      },
      {
        Header: t('Current Status'),
        id: 'status',
        disableSortBy: true,
        accessor: (row) => row,
        cellRenderer: (row) => <StatusCellRenderer status={row.currentStatus} />,
      },
      {
        Header: t('Patient'),
        id: 'patient',
        disableSortBy: true,
        accessor: (row) => row,
        cellRenderer: (row) => <PatientCellRenderer row={row} />,
      },
      {
        Header: <TableHeaderWithTimezoneInfo title={t('Appointment Time')} locationId={locationId} />,
        id: 'appointmentTime',
        disableSortBy: true,
        minWidth: 220,
        accessor: (row) => row,
        cellRenderer: (row) => <AppointmentTimeCellRenderer row={row} locationTimezone={locationTimezone} />,
      },
      {
        Header: t('Service'),
        id: 'service',
        disableSortBy: true,
        omit: !showServiceColumn,
        accessor: (row) => row,
        cellRenderer: (row) => <ServiceAndAppointmentTypeCellRenderer row={row} isService />,
      },
      {
        Header: t('Appointment Type'),
        id: 'appointment-type',
        disableSortBy: true,
        accessor: (row) => row,
        cellRenderer: (row) => <ServiceAndAppointmentTypeCellRenderer row={row} />,
      },
      {
        Header: t('Source'),
        id: 'activitySourceType',
        disableSortBy: true,
        accessor: (row) => row,
        cellRenderer: (row) => <SourceCellRenderer row={row} />,
      },
    ],
    [locationId, locationTimezone, showServiceColumn, t]
  );

  const tableActions: TableActionsProps<ScheduleActivityItem> = [
    {
      type: 'icon',
      label: t('Refresh Data'),
      Icon: () => <Icon name='update' />,
      onClick: onRefetch,
    },
  ];

  const rowActions = {
    onRowClick: (row: ScheduleActivityItem) => {
      if (!isRowClickDisabled(row)) {
        onRowClick(row);
      }
    },
  };

  const customToolbarRender = () => {
    return locationDropdown;
  };

  const manualFiltersRender: ManualFilters<ScheduleActivityItem> = (modalProps, setShowEnabledFiltersBadge) => {
    // to show blue dot to indicate active filters
    setShowEnabledFiltersBadge(hasActiveFilters(filters));

    return modalProps.show ? (
      <FiltersDrawer
        {...modalProps}
        filters={filters}
        filterOptions={filterOptions}
        setFilters={setFilters}
        setShowEnabledFiltersBadge={setShowEnabledFiltersBadge}
        trackFiltersApplied={trackFiltersApplied}
      />
    ) : undefined;
  };

  const manualPaginationConfig = {
    page: pageConfig.page,
    defaultRowsPerPage: pageConfig.limit,
    rowsPerPageOptions: [10, 25, 50, 100],
    hasNext: pageConfig.page < Math.ceil((totalItems ?? 0) / pageConfig.limit),
    hasPrevious: pageConfig.page > 1,
    total: Math.ceil((totalItems ?? 0) / pageConfig.limit),
    onNumRowsChange: (pageSize: number) => setPageConfig((prev) => ({ ...prev, limit: pageSize, page: 1 })),
    handleChange: (pageNumber: number) => setPageConfig((prev) => ({ ...prev, page: pageNumber })),
  };

  const styleConfig = {
    rows: (row: ScheduleActivityItem) =>
      isRowClickDisabled(row) &&
      css`
        cursor: not-allowed;
      `,
  };

  const emptyStateConfig = {
    type: 'schedule_alerts' as const,
    header: t('Your schedule activity is empty'),
    description: t('Schedule an appointment to begin building your practice history.'),
  };

  return (
    <Table
      data={data}
      isLoading={isLoading}
      colConfig={columnConfig}
      tableActions={tableActions}
      rowActions={rowActions}
      styleConfig={styleConfig}
      customToolbarRender={customToolbarRender}
      manualFiltersRender={manualFiltersRender}
      isPaginated
      manualPaginationConfig={manualPaginationConfig}
      emptyStateConfig={emptyStateConfig}
      // TODO: Add search when API supports it.
    />
  );
};

const isRowClickDisabled = (row: ScheduleActivityItem) => {
  return row.activityType === 'REQUESTED' && !ACTIVITY_ROW_CLICK_ALLOWED_STATUSES.includes(row.currentStatus ?? '');
};
